import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import Toggle from 'react-toggle';
import PageLayout from '../../components/PageLayout';
import ActionLoader from '../../components/ActionLoader';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import Toast from '../../components/Toast';
import FormInput from '../../components/FormInput';
import HeaderIconSvg from '../../icons/newicons/arrow-down-up.svg';
import {
  getData,
  putData,
  GET_USER_DETAILS_URL,
  GET_USER_KYC_RESPONSE_URL,
  APPROVE_USER_ACCOUNT_URL,
  GET_COMPANY_DETAILS_URL,
  UPDATE_COMPANY_DETAILS_URL,
} from '../../utils/api';
import KycQuestionnaireView from './KycQuestionnaireView';
import '../Dashboard/Dashboard.css';
import './log-user-detail.css';

const normalizeDetailKey = (k) => k.toLowerCase().replace(/_/g, '');

/** Keys hidden on the log user detail screen (permission / internal flags). */
const HIDDEN_USER_DETAIL_KEYS = new Set([
  'hierarchy',
  'issuperadmin',
  'ispicturemandatory',
  'canseeunitprice',
  'canseemarkup',
  'canseesubtotal',
  'canbuildcart',
  'accounttype',
  'isnewuser',
  'selfsignup',
  'isselfsignup',
  'userid',
  'companyid',
  'entityid',
  'id',
  'createddate',
  'datecreated',
  'createdat',
  'usercreated',
  'signupdate',
  'registeredat',
  'created',
  'ein',
  'employeridentificationnumber',
  'taxid',
  'businesslicense',
  'businesslicenseurl',
  'updateddate',
  'updatedat',
  'dateupdated',
  'lastupdated',
  'modifiedat',
  'modifieddate',
  'lastmodified',
  'lastmodifieddate',
]);

const formatLabel = (key) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

/** Company id for KYC API query (same fallbacks as summary). */
/** `true` / `false` when set; `null` if absent (approve button stays hidden). */
const getAccountApprovedFromPayload = (p) => {
  if (!p || typeof p !== 'object') return null;
  const v =
    p.isAccountApproved ??
    p.is_account_approved ??
    p.accountApproved ??
    p.account_approved;
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no') return false;
  }
  return null;
};

const getCompanyIdFromPayload = (p) => {
  if (!p || typeof p !== 'object') return '';
  const v =
    p.companyId ??
    p.company_id ??
    p.entityId ??
    p.entity_id ??
    p.organizationId ??
    p.organization_id;
  if (v === undefined || v === null) return '';
  const s = String(v).trim();
  return s || '';
};

const CREATED_DATE_KEYS = new Set([
  'createddate',
  'datecreated',
  'createdat',
  'usercreated',
  'signupdate',
  'registeredat',
]);

const toDateFromValue = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value > 1e12) return new Date(value);
    if (value > 1e9 && value < 1e11) return new Date(value * 1000);
    return null;
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if (/^\d{10,16}$/.test(t)) {
      const n = Number(t);
      const ms = t.length >= 13 || n > 1e12 ? n : n * 1000;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(t)) {
      const d = new Date(t);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  return null;
};

const formatCreatedDateDisplay = (key, value) => {
  const nk = normalizeDetailKey(key);
  const isCreatedKey = CREATED_DATE_KEYS.has(nk) || nk === 'created';
  if (!isCreatedKey) return null;
  const d = toDateFromValue(value);
  if (!d) return null;
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
};

/** First matching key wins for summary “Joined date”. */
const JOIN_SUMMARY_DATE_KEY_ORDER = [
  'createdDate',
  'created_date',
  'createdAt',
  'dateCreated',
  'userCreated',
  'signUpDate',
  'registeredAt',
  'created',
];

const formatScalar = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return d.toLocaleString();
    }
    return value;
  }
  return String(value);
};

/** Normalized keys (see normalizeDetailKey) that show image/link preview when value is an http(s) URL. */
const MEDIA_PREVIEW_DETAIL_KEYS = new Set([
  'profileurl',
  'driverslicenseurl',
  'businesslicense',
  'businesslicenseurl',
]);

const isHttpUrlString = (value) =>
  typeof value === 'string' && /^https?:\/\//i.test(value.trim());

const MediaUrlFieldValue = ({ url }) => {
  const trimmed = url.trim();
  const [showImg, setShowImg] = useState(true);

  return (
    <div className="log-user-detail__media-preview">
      {showImg ? (
        <div className="log-user-detail__media-preview-frame">
          <img
            src={trimmed}
            alt=""
            className="log-user-detail__media-preview-img"
            onError={() => setShowImg(false)}
          />
        </div>
      ) : (
        <div className="log-user-detail__media-preview-fallback">Preview not available for this file type.</div>
      )}
      <a
        href={trimmed}
        target="_blank"
        rel="noopener noreferrer"
        className="log-user-detail__media-preview-link"
      >
        Open in new tab
      </a>
    </div>
  );
};

const parseGroupModifierIsPercentage = (v) => {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no') return false;
  }
  return null;
};

const businessDisplayFromCompany = (d) => {
  if (!d || typeof d !== 'object') {
    return {
      companyName: '',
      ein: '',
      licensePath: null,
      licenseUrl: null,
      groupModifier: null,
      groupModifierIsPercentage: null,
    };
  }
  const licensePath = d.businessLicense ?? d.business_license ?? null;
  const licenseUrl = d.businessLicenseUrl ?? d.business_license_url ?? null;
  const rawModifier = d.groupModifier ?? d.group_modifier;
  const groupModifier =
    rawModifier === undefined || rawModifier === null || rawModifier === ''
      ? null
      : rawModifier;
  return {
    companyName: d.companyName ?? d.company_name ?? d.name ?? '',
    ein: d.ein ?? d.EIN ?? d.employerIdentificationNumber ?? d.taxId ?? d.tax_id ?? '',
    licensePath,
    licenseUrl,
    groupModifier,
    groupModifierIsPercentage: parseGroupModifierIsPercentage(
      d.groupModifierIsPercentage ?? d.group_modifier_is_percentage
    ),
  };
};

const renderBusinessLicensePreview = (raw) => {
  if (raw === null || raw === undefined || String(raw).trim() === '') return null;
  const s = String(raw).trim();
  if (isHttpUrlString(s)) return <MediaUrlFieldValue url={s} />;
  return (
    <div className="log-user-detail__license-url-fallback">
      <a href={s} target="_blank" rel="noopener noreferrer" className="log-user-detail__media-preview-link">
        Open license file
      </a>
    </div>
  );
};

const DETAIL_TABS = [
  { id: 'basic', label: 'BASIC' },
  { id: 'kyc', label: 'KYC' },
  { id: 'business', label: 'BUSINESS' },
];

const LogUserDetail = () => {
  const { userId } = useParams();
  const location = useLocation();
  const logsBackTo = `/config/logs${(location.state && location.state.logsSearch) || ''}`;
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailTab, setDetailTab] = useState('basic');
  const [kycLoading, setKycLoading] = useState(false);
  const [kycError, setKycError] = useState(null);
  const [kycPayload, setKycPayload] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [toastParams, setToastParams] = useState({ message: '', type: '', id: '' });
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessError, setBusinessError] = useState(null);
  const [businessPayload, setBusinessPayload] = useState(null);
  const [modifierIsPercentage, setModifierIsPercentage] = useState(true);
  const [modifierInputValue, setModifierInputValue] = useState('');
  const [modifierSaving, setModifierSaving] = useState(false);
  const lastBasicFetchUserIdRef = useRef(undefined);

  /** Refetch whenever the BASIC tab is active (including returning from KYC / BUSINESS). */
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setError('Missing user identifier.');
      return undefined;
    }
    if (detailTab !== 'basic') {
      return undefined;
    }

    const userIdChanged = lastBasicFetchUserIdRef.current !== userId;
    lastBasicFetchUserIdRef.current = userId;

    let cancelled = false;
    if (userIdChanged) {
      setLoading(true);
    }
    setError(null);

    const run = async () => {
      try {
        const data = await getData(GET_USER_DETAILS_URL, { userId });
        if (!cancelled) {
          setPayload(data && typeof data === 'object' && data.data !== undefined ? data.data : data);
        }
      } catch {
        if (!cancelled) {
          setError('Could not load user details. You may not have access, or the user may not exist.');
          setPayload(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [userId, detailTab]);

  useEffect(() => {
    setDetailTab('basic');
    setKycPayload(null);
    setKycError(null);
    setKycLoading(false);
    setBusinessPayload(null);
    setBusinessError(null);
    setBusinessLoading(false);
    setModifierIsPercentage(true);
    setModifierInputValue('');
    setModifierSaving(false);
  }, [userId]);

  useEffect(() => {
    if (detailTab !== 'kyc' || !userId) {
      return undefined;
    }
    const companyId = getCompanyIdFromPayload(payload);
    if (!companyId) {
      setKycLoading(false);
      setKycError('Company ID is missing from user details; cannot load KYC.');
      setKycPayload(null);
      return undefined;
    }
    let cancelled = false;
    setKycLoading(true);
    setKycError(null);
    const run = async () => {
      try {
        const data = await getData(GET_USER_KYC_RESPONSE_URL, { userId, companyId });
        if (!cancelled) {
          const normalized =
            data && typeof data === 'object' && data.data !== undefined ? data.data : data;
          setKycPayload(normalized ?? null);
        }
      } catch (e) {
        if (!cancelled) {
          setKycPayload(null);
          setKycError(
            e?.response?.data?.error ||
              e?.response?.data?.message ||
              e?.message ||
              'Could not load KYC response.'
          );
        }
      } finally {
        if (!cancelled) {
          setKycLoading(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [detailTab, userId, payload]);

  useEffect(() => {
    if (detailTab !== 'business') {
      return undefined;
    }
    const companyId = getCompanyIdFromPayload(payload);
    if (!companyId) {
      setBusinessLoading(false);
      setBusinessError('Company ID is missing from user details; cannot load business.');
      setBusinessPayload(null);
      return undefined;
    }
    let cancelled = false;
    setBusinessLoading(true);
    setBusinessError(null);
    const run = async () => {
      try {
        const data = await getData(GET_COMPANY_DETAILS_URL, { companyId });
        if (!cancelled) {
          const normalized =
            data && typeof data === 'object' && data.data !== undefined ? data.data : data;
          setBusinessPayload(normalized ?? null);
        }
      } catch (e) {
        if (!cancelled) {
          setBusinessPayload(null);
          setBusinessError(
            e?.response?.data?.error ||
              e?.response?.data?.message ||
              e?.message ||
              'Could not load company details.'
          );
        }
      } finally {
        if (!cancelled) {
          setBusinessLoading(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [detailTab, payload]);

  const titleName = useMemo(() => {
    if (!payload || typeof payload !== 'object') return null;
    const fn = payload.firstName || payload.firstname;
    const ln = payload.lastName || payload.lastname;
    if (fn || ln) return [fn, ln].filter(Boolean).join(' ');
    if (payload.name) return payload.name;
    if (payload.email) return payload.email;
    return null;
  }, [payload]);

  const companyTitle = useMemo(() => {
    if (!payload || typeof payload !== 'object') return null;
    const c =
      payload.companyName ??
      payload.company_name ??
      payload.entityName ??
      payload.entity_name ??
      payload.businessName ??
      payload.organizationName ??
      payload.organization_name;
    if (c !== undefined && c !== null && String(c).trim() !== '') return String(c).trim();
    return null;
  }, [payload]);

  const companyIdSummary = useMemo(() => {
    if (!payload || typeof payload !== 'object') return null;
    const v =
      payload.companyId ??
      payload.company_id ??
      payload.entityId ??
      payload.entity_id ??
      payload.organizationId ??
      payload.organization_id;
    if (v === undefined || v === null || String(v).trim() === '') return null;
    return String(v).trim();
  }, [payload]);

  const routeUserIdDisplay = useMemo(() => {
    if (!userId) return '';
    try {
      return decodeURIComponent(userId);
    } catch {
      return userId;
    }
  }, [userId]);

  const joinedDateDisplay = useMemo(() => {
    if (!payload || typeof payload !== 'object') return null;
    for (let i = 0; i < JOIN_SUMMARY_DATE_KEY_ORDER.length; i += 1) {
      const key = JOIN_SUMMARY_DATE_KEY_ORDER[i];
      const raw = payload[key];
      if (raw === undefined || raw === null || raw === '') continue;
      const formatted = formatCreatedDateDisplay(key, raw);
      if (formatted) return formatted;
    }
    return null;
  }, [payload]);

  const rows = useMemo(() => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return [];
    return Object.entries(payload).filter(([k]) => {
      if (k === 'data') return false;
      return !HIDDEN_USER_DETAIL_KEYS.has(normalizeDetailKey(k));
    });
  }, [payload]);

  const businessFields = useMemo(() => businessDisplayFromCompany(businessPayload), [businessPayload]);

  useEffect(() => {
    if (!businessPayload || typeof businessPayload !== 'object') return;
    const fields = businessDisplayFromCompany(businessPayload);
    setModifierIsPercentage(fields.groupModifierIsPercentage !== false);
    setModifierInputValue(fields.groupModifier == null ? '' : String(fields.groupModifier));
  }, [businessPayload]);

  const modifierDirty = useMemo(() => {
    const savedModifier =
      businessFields.groupModifier == null ? '' : String(businessFields.groupModifier);
    const savedIsPercentage = businessFields.groupModifierIsPercentage !== false;
    return (
      String(modifierInputValue) !== savedModifier ||
      modifierIsPercentage !== savedIsPercentage
    );
  }, [businessFields, modifierInputValue, modifierIsPercentage]);

  const handleSavePriceModifier = async () => {
    const companyId = getCompanyIdFromPayload(payload);
    if (!companyId) {
      setToastParams({
        type: 'ERROR',
        message: 'Company ID is missing; cannot update price modifier.',
        id: String(Date.now()),
      });
      return;
    }
    const trimmed = String(modifierInputValue).trim();
    const groupModifier =
      trimmed === '' ? '' : Number.isFinite(Number(trimmed)) ? Number(trimmed) : trimmed;
    setModifierSaving(true);
    try {
      await putData(UPDATE_COMPANY_DETAILS_URL, {
        companyId,
        groupModifier,
        groupModifierIsPercentage: modifierIsPercentage,
      });
      setBusinessPayload((prev) =>
        prev && typeof prev === 'object'
          ? {
              ...prev,
              groupModifier,
              groupModifierIsPercentage: modifierIsPercentage,
            }
          : prev
      );
      setToastParams({
        type: 'SUCCESS',
        message: 'Price modifier updated.',
        id: String(Date.now()),
      });
    } catch (e) {
      setToastParams({
        type: 'ERROR',
        message:
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          'Could not update price modifier.',
        id: String(Date.now()),
      });
    } finally {
      setModifierSaving(false);
    }
  };

  const summaryBits = useMemo(() => {
    if (!payload || typeof payload !== 'object') return [];
    const bits = [];
    const em = payload.email;
    const ph = payload.phone;
    if (em) bits.push({ label: 'Email', value: em });
    if (ph) bits.push({ label: 'Phone', value: ph });
    const addr = payload.address;
    if (addr && String(addr).trim()) bits.push({ label: 'Address', value: String(addr).trim() });
    const city = payload.city;
    const state = payload.state;
    const zip = payload.zipcode || payload.pincode;
    const line = [city, state].filter(Boolean).join(', ') + (zip ? ` ${zip}` : '');
    if (line.trim()) bits.push({ label: 'Location', value: line.trim() });
    return bits;
  }, [payload]);

  const approveUserIdParam = routeUserIdDisplay || userId || '';

  const accountApprovedResolved = useMemo(() => getAccountApprovedFromPayload(payload), [payload]);
  const showApproveAccountButton = accountApprovedResolved === false;

  const handleConfirmApproveAccount = async () => {
    if (!approveUserIdParam) return;
    setApproveSubmitting(true);
    try {
      await getData(APPROVE_USER_ACCOUNT_URL, { userId: approveUserIdParam });
      setShowApproveModal(false);
      setPayload((prev) =>
        prev && typeof prev === 'object' && !Array.isArray(prev)
          ? { ...prev, isAccountApproved: true, is_account_approved: true }
          : prev
      );
      setToastParams({
        type: 'SUCCESS',
        message: 'Account approved.',
        id: String(Date.now()),
      });
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        'Could not approve account.';
      setToastParams({
        type: 'ERROR',
        message: msg,
        id: String(Date.now()),
      });
    } finally {
      setApproveSubmitting(false);
    }
  };

  const renderValue = (key, value) => {
    if (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      !(value instanceof Date)
    ) {
      return <pre className="log-user-detail-json">{JSON.stringify(value, null, 2)}</pre>;
    }
    if (Array.isArray(value)) {
      return <pre className="log-user-detail-json">{JSON.stringify(value, null, 2)}</pre>;
    }
    const createdStr = formatCreatedDateDisplay(key, value);
    if (createdStr) return createdStr;
    const nk = normalizeDetailKey(key);
    if (MEDIA_PREVIEW_DETAIL_KEYS.has(nk) && isHttpUrlString(value)) {
      return <MediaUrlFieldValue url={value} />;
    }
    return formatScalar(value);
  };

  return (
    <PageLayout icon={HeaderIconSvg} heading="User details" hideSearchBar>
      <div className="log-user-detail">
        <div className="log-user-detail__nav">
          <Link to={logsBackTo} className="c-gray fs-14 fw-bold c-pointer">
            ← Back to Logs
          </Link>
        </div>

        <div className="p-relative log-user-detail__panel log-user-detail__panel--card">
          <div className="log-user-detail__panel-body">
            {loading && (
              <div className="p-relative" style={{ minHeight: 220 }}>
                <ActionLoader />
              </div>
            )}

            {!loading && error && (
              <div className="log-user-detail__state log-user-detail__state--error">{error}</div>
            )}

            {!loading && !error && rows.length === 0 && (
              <div className="log-user-detail__state c-gray fs-14">No fields returned for this user.</div>
            )}

            {!loading && !error && rows.length > 0 && (
              <>
                <div className="log-user-detail__summary">
                  <div className="log-user-detail__summary-head">
                    <div className="log-user-detail__summary-titlewrap">
                      <h2 className="log-user-detail__summary-name">
                        <span className="log-user-detail__summary-name-text">{titleName || 'User'}</span>
                        {companyTitle ? (
                          <>
                            <span className="log-user-detail__summary-title-sep"> | </span>
                            <span className="log-user-detail__summary-company-inline">{companyTitle}</span>
                          </>
                        ) : null}
                      </h2>
                    </div>
                    <div className="log-user-detail__summary-actions">
                      {joinedDateDisplay ? (
                        <>
                          <span className="log-user-detail__summary-joined-tr-label">Joined date</span>
                          <span className="log-user-detail__summary-joined-tr-value">{joinedDateDisplay}</span>
                        </>
                      ) : null}
                      {showApproveAccountButton ? (
                        <button
                          type="button"
                          className="log-user-detail__approve-account-btn"
                          onClick={() => setShowApproveModal(true)}
                          disabled={approveSubmitting}
                        >
                          Approve Account
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {summaryBits.length > 0 && (
                    <div className="log-user-detail__summary-meta">
                      {summaryBits.map(({ label, value }, idx) => (
                        <span key={`${label}-${idx}`}>
                          <strong>{label}</strong>
                          {': '}
                          {value}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="log-user-detail__summary-ids">
                    <span>User ID: {routeUserIdDisplay || '—'}</span>
                    {companyIdSummary ? (
                      <>
                        <span className="log-user-detail__summary-ids-sep" aria-hidden>
                          ·
                        </span>
                        <span>Company ID: {companyIdSummary}</span>
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="log-user-detail__fields-section">
                  <div className="log-user-detail__tabs" role="tablist" aria-label="User detail sections">
                    {DETAIL_TABS.map(({ id, label }, index) => (
                      <React.Fragment key={id}>
                        {index > 0 ? (
                          <span className="log-user-detail__tab-sep" aria-hidden="true">
                            |
                          </span>
                        ) : null}
                        <button
                          type="button"
                          role="tab"
                          aria-selected={detailTab === id}
                          id={`log-user-detail-tab-${id}`}
                          className={`log-user-detail__tab${detailTab === id ? ' log-user-detail__tab--active' : ''}`}
                          onClick={() => setDetailTab(id)}
                        >
                          {label}
                        </button>
                      </React.Fragment>
                    ))}
                  </div>

                  {detailTab === 'basic' && (
                    <div
                      className="log-user-detail__tab-panel"
                      role="tabpanel"
                      aria-labelledby="log-user-detail-tab-basic"
                    >
                      <div className="log-user-detail__grid">
                        {rows.map(([key, value]) => (
                          <div className="log-user-detail__field" key={key}>
                            <div className="log-user-detail__field-label">{formatLabel(key)}</div>
                            <div className="log-user-detail__field-value">{renderValue(key, value)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {detailTab === 'kyc' && (
                    <div
                      className="log-user-detail__tab-panel log-user-detail__tab-panel--kyc"
                      role="tabpanel"
                      aria-labelledby="log-user-detail-tab-kyc"
                    >
                      {kycLoading && (
                        <div className="p-relative log-user-detail__kyc-loader">
                          <ActionLoader />
                        </div>
                      )}
                      {!kycLoading && kycError && (
                        <div className="log-user-detail__kyc-message log-user-detail__kyc-message--error">
                          {kycError}
                        </div>
                      )}
                      {!kycLoading && !kycError && kycPayload !== null && (
                        <KycQuestionnaireView data={kycPayload} userContext={payload} />
                      )}
                      {!kycLoading && !kycError && kycPayload === null && (
                        <div className="log-user-detail__kyc-message c-gray fs-14">No KYC response data.</div>
                      )}
                    </div>
                  )}

                  {detailTab === 'business' && (
                    <div
                      className="log-user-detail__tab-panel log-user-detail__tab-panel--business"
                      role="tabpanel"
                      aria-labelledby="log-user-detail-tab-business"
                    >
                      {businessLoading && (
                        <div className="p-relative log-user-detail__kyc-loader">
                          <ActionLoader />
                        </div>
                      )}
                      {!businessLoading && businessError && (
                        <div className="log-user-detail__kyc-message log-user-detail__kyc-message--error">
                          {businessError}
                        </div>
                      )}
                      {!businessLoading && !businessError && businessPayload !== null && (
                        <div className="log-user-detail__grid log-user-detail__grid--business">
                          <div className="log-user-detail__field">
                            <div className="log-user-detail__field-label">Company name</div>
                            <div className="log-user-detail__field-value">
                              {businessFields.companyName ? formatScalar(businessFields.companyName) : '—'}
                            </div>
                          </div>
                          <div className="log-user-detail__field">
                            <div className="log-user-detail__field-label">EIN</div>
                            <div className="log-user-detail__field-value">
                              {businessFields.ein ? formatScalar(businessFields.ein) : '—'}
                            </div>
                          </div>
                          <div className="log-user-detail__field log-user-detail__field--full-row">
                            <div className="log-user-detail__field-label">Price Modifier</div>
                            <div className="log-user-detail__field-value">
                              <div className="log-user-detail__price-modifier">
                                <div className="log-user-detail__price-modifier-toggle flex flex-middle">
                                  <span className="c-gray mr-4">%</span>
                                  <Toggle
                                    className="bg-theme hide-check-cross"
                                    checked={!modifierIsPercentage}
                                    disabled={modifierSaving}
                                    onChange={(e) => {
                                      setModifierIsPercentage(!e.target.checked);
                                    }}
                                  />
                                  <span className="c-gray ml-4">$</span>
                                </div>
                                <FormInput
                                  inputClassName="fs-16 rounded-pill"
                                  className="hideSpinner wt-120 log-user-detail__price-modifier-input"
                                  type="number"
                                  step="0.01"
                                  value={modifierInputValue}
                                  onChange={(e) => setModifierInputValue(e.target.value)}
                                />
                                <button
                                  type="button"
                                  className="log-user-detail__price-modifier-save"
                                  onClick={handleSavePriceModifier}
                                  disabled={modifierSaving || !modifierDirty}
                                >
                                  {modifierSaving ? '…' : 'Save'}
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="log-user-detail__field log-user-detail__field--full-row">
                            <div className="log-user-detail__field-label">Business license</div>
                            <div className="log-user-detail__field-value">
                              {businessFields.licensePath != null && String(businessFields.licensePath).trim() !== ''
                                ? formatScalar(businessFields.licensePath)
                                : '—'}
                            </div>
                          </div>
                          {businessFields.licenseUrl != null && String(businessFields.licenseUrl).trim() !== '' ? (
                            <div className="log-user-detail__field log-user-detail__field--full-row">
                              <div className="log-user-detail__field-label">Business license preview</div>
                              <div className="log-user-detail__field-value">
                                {renderBusinessLicensePreview(businessFields.licenseUrl)}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      )}
                      {!businessLoading && !businessError && businessPayload === null && (
                        <div className="log-user-detail__kyc-message c-gray fs-14">No company data returned.</div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {toastParams.message ? (
        <Toast type={toastParams.type} message={toastParams.message} id={toastParams.id} />
      ) : null}

      {showApproveModal ? (
        <Modal onClose={() => !approveSubmitting && setShowApproveModal(false)}>
          <div className="pl-16 pr-16">
            <div className="fs-18 fw-bolder t-center mb-20">
              {`Approve ${titleName || 'this user'}'s account? This cannot be undone from here.`}
            </div>
            <div className="flex flex-middle flex-center">
              <Button
                className="mr-20 btn ht-40 btn-red"
                onClick={() => !approveSubmitting && setShowApproveModal(false)}
                disabled={approveSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="modalSave"
                className="btn ht-40"
                onClick={handleConfirmApproveAccount}
                disabled={approveSubmitting}
              >
                {approveSubmitting ? 'Submitting…' : 'Confirm'}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </PageLayout>
  );
};

export default LogUserDetail;
